import { Component, Input, OnInit, Output, EventEmitter, ViewChild, OnDestroy, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { DynamicComponentDirective } from '../../../directives/dynamic-component.directive';
import * as Models from '../../../models/models-index';
import { PanelElementResolverService } from '../panel-element-resolver.service';
import { MatSelectChange } from '@angular/material/select';
import * as SharedServices from '../../../../_shared/services/services-index';
import { BaseComponent } from '../../../../_shared/components/base.component';
import { AppState } from '../../../../_store/app-state.model';
import { Store } from '@ngrx/store';
import { Enums, IEnums } from '../../../../_shared/enums/enums';
import { IRole } from '../../../models/models-index';
import { filter, map, tap, switchMap, catchError, takeUntil, first } from 'rxjs/operators';
import { ReportsService } from '../../../services/api/reports.service';
import { ExportCsv } from '../../../../_shared/utilities/generate-csv';
import { AppSelectors } from '../../../../_store/selector-types';
import { convertOptionsToDictionary } from '../../panel-elements/panel-utils';
import { SelectableItem } from '../../nested-menu/nested-menu.component';
import { tableOptions, totalRowScenarios } from '../../../constants/constants';
import { FeatureFlags } from '../../../services/config/feature.flags';
import { FeatureFlagStatus } from '../../../services/config/runtime-config.service';
const clone = require('rfdc/default');

type SelectedConfiguration = { panelConfiguration: Models.PanelConfiguration, columnSet: string, options: Models.OptionModel[] }

@Component({
    selector: 'full-width-data-panel',
    templateUrl: './full-width-data-panel.component.html',
    styleUrls: ['./full-width-data-panel.component.scss'],
    standalone: false
})

export class FullWidthDataPanelComponent extends BaseComponent implements OnInit, OnDestroy, Models.PanelComponent {

  @ViewChild(DynamicComponentDirective, { static: true }) dynamicComponent!: DynamicComponentDirective;
  // @Output() openHelpText = new EventEmitter();
  @Output() selectionChange: EventEmitter<string> = new EventEmitter<string>();
  @Input() panelConfig: Models.Panel;

  private _dataSets: Models.DataSet[] = [];
  @Input()
  set dataSets(value: Models.DataSet[]) {
    this._dataSets = value;

    if (value?.length > 0) {
      if (this.selectedConfiguration$.value) {
        // Re-run the pipeline for the current configuration
        this.selectedConfiguration$.next({ ...this.selectedConfiguration$.value });
      } else {
        // Initialize if no configuration exists
        this.selectedConfiguration$.next(this.getInitialData());
      }
      this.mapPanelConfigToSelectableItems();
      return;
    }

    this.dataTableService.clearData();
  }

  get dataSets(): Models.DataSet[] {
    return this._dataSets;
  }

  @Input() rowPanelId: string;
  @Input() viewContext: Models.ViewContext;

  enums: IEnums = Enums;
  locale: string;
  subscriptions: Subscription[] = [];
  filterModel: Models.IFilterModel;
  tableTrendPlaceholder: string;
  dataTableType: string;
  get panelConfigs() { return this.panelConfig.configurations }
  selectedConfiguration$ = new BehaviorSubject<SelectedConfiguration>(null);
  get selectedConfiguration() { return this.selectedConfiguration$.value }
  settings: Models.OptionModel[] = [];
  columnSets: string[] = [];
  selectedColumnSet: Models.ColumnSet;
  private _components: Models.ElementComponent[] = [];
  translationService: Models.ITranslationService;

  panelAvailableIndexes: SelectableItem[] = [];
  get hasPanelAvailableIndexes(): boolean {
    return this.panelAvailableIndexes && this.panelAvailableIndexes.length > 0;
  }
  selectedItem: SelectableItem;

  destroySubject = new Subject<void>();
  destroyed$ = this.destroySubject.asObservable();

  constructor(
    private store$: Store<AppState>,
    private localeService: SharedServices.LocaleService,
    private resolverService: PanelElementResolverService,
    private dataTableService: SharedServices.DataTableService,
    private filterService: SharedServices.FilterService,
    private reportService: ReportsService,
    private helpTextService: SharedServices.HelpTextService,
    private heatmapService: SharedServices.HeatmapService,
    private runtimeConfigService: SharedServices.RuntimeConfigService,
  ) {
    super();
    this.tableTrendPlaceholder = 'Default';
  }

  ngOnInit(): void {
    //this.emitSelectedMetric(this.indexConfig.defaultIndexCode);
    // this.filterService.filter$.subscribe(filter => {
    //   this.filterModel = filter;
    // });

    this.subscriptions.push(
      this.localeService.locale$.subscribe(loc => this.locale = loc),
      this.generateElements().subscribe()
    )

    // initialize selectedConfiguration based on panel config
    this.createElements(this.getInitialData());
    this.mapPanelConfigToSelectableItems();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  resolveElement(config: SelectedConfiguration) {
    const { panelConfiguration, columnSet } = config;

    if (!columnSet || panelConfiguration.elements.length === 1)
      return panelConfiguration.elements[0];
    else
      return panelConfiguration.elements.find(e => !!columnSet && e.settings.columnSetNames.find(n => n === columnSet)) ?? panelConfiguration.elements[0];
  }

  generateElements() {
    const elementPipeline = this.selectedConfiguration$.pipe(
      filter(config => !!config),
      map(config => {
        const element = this.resolveElement(config);

        this.createElements(config);

        return ({ element, config })
      }),
      switchMap(({ element, config }) => this.loadDataSet((element.settings.dataSets ?? [element.settings.dataSet])[0]).pipe(
        first(),
        map(ds => ({ ds, config, element }))  
      )),
      map(({ ds, config, element }) => {
        const dataset = clone(ds) as Models.DataSet;
        // find the appropriate element component based on the selected configuration
        const componentRef = this._components.find(c => this.setsAreEqual(c.settings.dataSets ?? [c.settings.dataSet], element.settings.dataSets ?? [element.settings.dataSet]));
        this.selectedColumnSet = (!!config.columnSet ? dataset.columnSets.find(cs => cs.name === config.columnSet) : null)
          ?? dataset.columnSets[0];

        if (!!element.settings.columnSetNames) {
          // we assume the data set names are being specified in the element settings
          this.columnSets = config.panelConfiguration.elements.reduce((acc, element) => {
            return element.settings.columnSetNames ? acc.concat(element.settings.columnSetNames) : acc;
          }, <string[]>[]);
        } else {
          this.columnSets = dataset.columnSets.map(cs => cs.name);
        }

        // enable settings if they were enabled for the previous data index
        const configOptions = config.options.map(option => {
          const existingSetting = this.settings.find(setting => setting.name === option.name);
          return {...option, enabled: existingSetting?.enabled ?? option.enabled};
        });

        this.settings = configOptions;
        // Detect 'expandable' column and scan dataset
        const expandableColIndex = dataset.columns.findIndex(c => c.name === 'expandable');

        // hack how to determine if the table is a hierarchy table
        const isHierarchyTable = expandableColIndex !== -1 && dataset.rows.some(row => {
            const cell = row[expandableColIndex] as Models.RowValue | undefined;

            const isTrue = (x: unknown): boolean => {
            if (typeof x === 'boolean') return x;
            if (typeof x === 'string') return x.trim().toLowerCase() === 'true';
            if (typeof x === 'number') return x === 1;
            return false;
            };

            return isTrue(cell?.value) || isTrue(cell?.label);
        });

        const isProbablyDate = (value: string): boolean => {
          return /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(value) ||
                /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$/.test(value);
        };

        // is trend table if more than 2 columns are date columns.
        const isTrendTable = dataset.columns.filter(c =>
          isProbablyDate(c.name) || isProbablyDate(c.displayName)
        ).length >= 2;

        const isTransactionTable = this.panelConfig.configurations.filter(config =>
          config.elements.filter(element =>
            element.type === "transaction-details-data-table"
          ).length > 0
        ).length > 0

        const hideHeatMapOption = isHierarchyTable || isTrendTable || isTransactionTable // <- this is bad, we need a better way to do this.
        const columns = this.configureTableColumns(element.settings, dataset, this.selectedColumnSet, isHierarchyTable || isTrendTable);
        const dataTableObject = {
          columns: columns,
          data: this.toRecords(dataset, columns, hideHeatMapOption),
          type: this.enums.dataTableTypes.hierarchy
        }

        this.dataTableService.updateData(dataTableObject);
        componentRef.dataSets = [dataset];
        this.notifyChildrenAboutUpdatedSettings();
      })
    );

    return elementPipeline;
  }

  private createElements(config: SelectedConfiguration ) {
    const viewContainerRef = this.dynamicComponent.viewContainerRef;
    viewContainerRef.clear();
    this._components = [];

    const element = this.resolveElement(config);
    const componentRef = this.resolverService.resolveElementComponent(viewContainerRef, element.type)

    componentRef.instance.settings = element.settings;
    componentRef.instance.panelConfiguration = config.panelConfiguration;
    componentRef.instance.dataSets = this._dataSets;
    this.dataTableType = element.type;
    this._components.push(componentRef.instance);
    //this._components.forEach(c => c.dataSets = null);
  }

  configureTableColumns(settings: Models.ElementSettings, dataset: Models.DataSet, selectedColumnSet: Models.ColumnSet, isSortingDisabled: boolean): Models.IDefaultTableColumn[] {
    const columns: Models.IDefaultTableColumn[] = [];

    // huge hack
    // check to see if we're missing any columns named 'entity'
    const firstDim = dataset.columns.find(c => c.type == 'dimension');
    if (firstDim && !dataset.columns.find(c => c.name == 'entity')) {
      firstDim.name = 'entity';
    }

    // add 'show' property to each column -- This is a hack I need to clean up
    dataset.columns.forEach(c => {
      c.show = true;
    });

    const isGlobalSortingDisabled = this.runtimeConfigService.isFeatureFlagEnabled(FeatureFlags.DisableGlobalSorting);

    const isSortingEnabled = !isSortingDisabled && !isGlobalSortingDisabled;

    // add dimension columns first
    dataset.columns.filter(c => c.type === 'dimension').forEach((col, index) => {
      const column: Models.IDefaultTableColumn = {
        columnDef: col.name,
        header: col.displayName ?? col.name,
        // header: col.header + columnSuffix,
        isMom: false,
        isYoy: false,
        show: true,
        print: col.show,
        clickable: !!false,
        columnClasses: null,
        sortable: col.sortable ?? isSortingEnabled,
        valueInverted: col.valueInverted,
        canDrillthrough: col.name == 'entity' ? (val) => false : (val) => this.canDrillthrough(this.filterModel, val.entityType),
        formatter: null,
        printFormatter: null
      };
      columns.push(column);
    });

    //const useColumnSet = selectedColumnSet ? selectedColumnSet : dataset.columnSets?.[0];
    const metricColumnsToAdd = this.selectedColumnSet ? this.selectedColumnSet.columnNames : dataset.columns.filter(c => c.type === 'metric').map(c => c.name);

    // add metric columns
    metricColumnsToAdd.forEach((col, index) => {
      const column = dataset.columns.find(c => c.name == col);
      if (column) {
        const col: Models.IDefaultTableColumn = {
          columnDef: column.name,
          header: column.displayName ?? column.name,
          isMom: false,
          isYoy: false,
          show: true,
          print: column.show,
          clickable: !!false,
          columnClasses: null,
          formatter: null,
          printFormatter: null,
          sortable: column.sortable ?? isSortingEnabled,
          valueInverted: column.valueInverted
        };
        columns.push(col);
      }
    });

    return columns;
  }
  removeAllDataSets() {
    this._dataSets = [];
    this.dataTableService.clearData();
  }
  loadDataSet(dataSetName: string): Observable<Models.DataSet> {
    // Check if the dataset with the given name exists in this.dataSets
    const existingDataSet = this.dataSets.find(
      (dataSet) => dataSet.name === dataSetName
    );

    // If the dataset exists, return it as an Observable
    if (existingDataSet) {
      return of(existingDataSet);
    }

    return this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      switchMap(routeData => this.getReportViewDataSet(routeData.reportName, dataSetName))
    );
  }

  getReportViewDataSet(reportName: string, dataSetName: string): Observable<Models.DataSet> {
    return this.filterService.getReportViewFilterRequestModel(reportName).pipe(
      takeUntil(this.destroyed$),
      map(requestModel => ({ ...requestModel, dataSets: [dataSetName], options: convertOptionsToDictionary(this.settings) })),
      switchMap(requestModel => this.reportService.getDataSets(requestModel)),
      map((dataSets: Models.DataSet[]) => {
        if (!dataSets || dataSets.length == 0)
          throw new Error('No data sets returned from the API');

        // dataSets.forEach(ds => {
        //   ds.columns.forEach(c => {
        //     const displayName = c.displayName ?? c.name
        //     c.displayName = c.type === 'metric'
        //       ? this.translationService?.getMetricNameTranslation(c.name, displayName, this.locale) ?? displayName
        //       : this.translationService?.getLabelTranslation(displayName, this.locale) ?? displayName
        //   });
        // })
        this._dataSets.push(dataSets[0]);

        return dataSets[0];
      }),
      tap({
        next: _ => _,
        error: err => {
          appInsights.trackException(err);
          // Rethrow the error to ensure it's propagated up the stream
          throw err;
        }
      }),
      catchError(err => {
        // Handle the error and return a safe value or an empty Observable
        // Example: return of({} as Models.DataSet);
        // Or rethrow the error if you want to handle it further up the chain
        return throwError(() => err);
      })
    );
  }

  toRecords(dataSet: Models.DataSet, columns: Models.IDefaultTableColumn[], isHeatmapDisabled: boolean): any[] {
    const columnNames = dataSet.columns.map(c => c.name);
    const sortValueSuffix = '__sortValue';
    const records = [];

    dataSet.rows.forEach(row => {
      const record = {};
      let foundShow = false;

      columnNames.forEach((name, i) => {
        const label = row[i]?.label;
        const value = row[i]?.value;

        if (name === 'show') foundShow = true;

        record[name] = label ?? value;
        record[`${name}${sortValueSuffix}`] = value ?? label ?? null;
      });

      if (!foundShow) record['show'] = true;

      records.push(record);
    });

    const totalRowPosition = this.resolveElement(this.selectedConfiguration).settings.options?.[totalRowScenarios.settingName];

    // Enable heatmap by default if not disabled explicitly.
    const isHeatmapGloballyDisabled = this.runtimeConfigService.isFeatureFlagEnabled(FeatureFlags.DisableGlobalHeatMapOption);

    const isHeatmapEnabled = !isHeatmapDisabled && !isHeatmapGloballyDisabled;

    if (isHeatmapEnabled) {
      this.heatmapService.precomputeHeatmapColors(records, dataSet, columns, columnNames, totalRowPosition);
      // add heatmap option to settings if it doesn't exist
      const heatmapSetting = this.settings.find(s => s.name === tableOptions.heatmap);

      if (!heatmapSetting) {
        this.settings.push({ name: tableOptions.heatmap, enabled: false });
      }
      else {
        heatmapSetting.enabled = false;
      }
    }
    else {
      // Removes heatmap option from settings if it exists.
      // Technically this should never happen,
      // but some tables have heatmap option added in config
      // though they shouldn't
      const heatmapIndex = this.settings.findIndex(s => s.name === tableOptions.heatmap);
      if (heatmapIndex !== -1) {
        this.settings.splice(heatmapIndex, 1);
      }
    }

    // Apply grey background color to the total row if totalRowPosition is set
    if (totalRowPosition) {
      // Apply grey background color to the existing total row (first or last)
      this.heatmapService.applyGreyBackgroundToTotalRow(records, totalRowPosition);
    }

    return records;
  }

  changeConfiguration($event: MatSelectChange) {
    // This is a necessary check for when we apply rule-based column set selection.  On load it works, but when
    // switching between indexes if we set the columnSet to null it will default to the first column set in the list
    // and ignore the rules.
    const defaultColumnSet = ($event.value.elements[0].settings.columnSetNames || []).length > 0
      ? $event.value.elements[0].settings.columnSetNames[0]
      : null;

    this.dataTableService.clearData();
    this.selectedConfiguration$.next({ panelConfiguration: $event.value, columnSet: defaultColumnSet, options: $event.value.options });
  }

  columnSetChanged($event): void {
    this.selectedConfiguration$.next({ ...this.selectedConfiguration, columnSet: $event.value });
  }

  private canDrillthrough(filterModel: Models.IFilterModel, level: string): boolean {
    
    const areSame = (s1: string, s2: string) => (s1 || '').toLowerCase() === (s2 || '').toLowerCase();

    // Determine if the user is in a specific role
    // const isRole = (roles: IRole[]) => roles.map(r => r.name.toLowerCase()).indexOf((filterModel.roleLevel || '').toLowerCase()) != -1;
    const isOrgLevel = (orgLevels:string[]) => orgLevels.some(l => this.runtimeConfigService.isInOrgRole(l, [filterModel.roleLevel]));

    // Determine if the role matches the requested report org level
    const isLevel = (orgLevel: string) => areSame(level, orgLevel);

    if (isOrgLevel(['dealerRoles'])) {
      return false;
    } else if (isOrgLevel(['org1Roles'])) {
      return isLevel(this.enums.hierarchicalEntityTypes.dealer.type);
    } else if (isOrgLevel(['org2Roles'])) {
      return isLevel(this.enums.hierarchicalEntityTypes.orgcode1.type)
        || isLevel(this.enums.hierarchicalEntityTypes.dealer.type);
    } else if (isOrgLevel(['org3Roles'])) {
      return isLevel(this.enums.hierarchicalEntityTypes.orgcode2.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode1.type)
        || isLevel(this.enums.hierarchicalEntityTypes.dealer.type);
    } else if (isOrgLevel(['org4Roles'])) {
      return isLevel(this.enums.hierarchicalEntityTypes.orgcode3.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode2.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode1.type)
        || isLevel(this.enums.hierarchicalEntityTypes.dealer.type);
    } else if (isOrgLevel(['org5Roles'])) {
      return isLevel(this.enums.hierarchicalEntityTypes.orgcode4.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode3.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode2.type)
        || isLevel(this.enums.hierarchicalEntityTypes.orgcode1.type)
        || isLevel(this.enums.hierarchicalEntityTypes.dealer.type);
    } else if (isOrgLevel(['systemAdministratorRoles', 'corporateRoles'])) {
      return true;
    } else {
      // we can't infer role appropriately so don't allow drill-through
      return false;
    }
  }

  applySetting($event, setting: Models.OptionModel) {
    setting.enabled = $event.checked;
    this.notifyChildrenAboutUpdatedSettings();

    if (setting.name === tableOptions.heatmap) {
      // we don't want to trigger data set reload
      return;
    }

    // make a copy since properties are readonly and we want to edit this copy later
    const currentSettings = this.selectedConfiguration?.options ? [...this.selectedConfiguration.options] : [];

    // add the setting if it isn't already in the list, otherwise update it
    if (!currentSettings.some(s => s.name == setting.name)) {
      currentSettings.push(setting);
    } else {
      currentSettings[currentSettings.findIndex(s => s.name == setting.name)] = setting;
    }

    this.removeAllDataSets();

    //this.settingsChangedSubject.next(this.selectedSettings);
    this.selectedConfiguration$.next({ ...this.selectedConfiguration, options: currentSettings });
  }

  isSettingSelected(setting: Models.OptionModel): boolean {
    return !!this.selectedConfiguration.options?.some(s => s.name == setting.name && s.enabled);
  }

  exportToExcel(selectedConfiguration: SelectedConfiguration) {
    const element = this.resolveElement(selectedConfiguration);
    const panelConfig = selectedConfiguration.panelConfiguration;

    // get dataset
    const dataSet = this.dataSets.find(ds => ds.name == element.settings.dataSet);

    // HACK - we're saying for now all 'meta' columns are not exportable
    const dimensions = [];
    const metrics = [];


    // build up dimensions/meta
    dataSet.columns.forEach((c, index) => {
      if (c.export != false && c.type != 'metric') {
        const displayName = c.displayName ?? c.name
        const column = {
          name: this.translationService?.getLabelTranslation(displayName, this.locale) ?? displayName,
          index: index,
          isMetric: false
        };
        dimensions.push(column)
      }
    });

    // build up metrics based on the order in this.selectedColumnSet.columns
    const columnNames = this.selectedColumnSet?.columnNames ?? dataSet.columns.map(c => c.name);
    columnNames.forEach(cn => {
      const c = dataSet.columns.find(c => c.name === cn);
      const cIndex = dataSet.columns.findIndex(c => c.name === cn);
      if (c.export != false && c.type === 'metric') {
        const column = {
          name: c.displayName ?? c.name,
          index: cIndex,
          isMetric: true
        };
        metrics.push(column);
      }
    });

    // Merge the two arrays
    const columns = dimensions.concat(metrics);

    const exportCols = {
      names: columns.map(c => c.name),
      indexes: columns.reduce((acc, c) => ({ ...acc, [c.name]: c.index }), {}),
      metrics: columns.reduce((acc, c) => ({ ...acc, [c.name]: c.isMetric }), {})
    };

    const { names: colNames, indexes: colIndexes, metrics: colMetrics } = exportCols;


    const rows = dataSet.rows.map(row => {
      return colNames.map(name => {
        const colIndex = colIndexes[name];
        const value = row[colIndex]?.value?.toString() || '';
        const label = row[colIndex]?.label?.toString() || '';
        const rowValue = label || value;

        let metricValue = colMetrics[name]
          ? rowValue
          : this.translationService?.getLabelTranslation(value || label, this.locale) || rowValue;

        if (metricValue === value) {
          // Use label if translation failed or was not needed
          metricValue = rowValue;
        }

        return metricValue;
      });
    });


    const panelLabel = panelConfig.label ?? panelConfig.name;
    const fileName = (panelLabel).replace(/-| /g, '');

    const exportData: string[][] = [[panelLabel]];
    exportData.push(colNames);
    exportData.push(...rows);

    new ExportCsv(exportData, fileName);

  }

  openHelpTextClicked(): void {
    this.helpTextService.openHelp(this.selectedConfiguration.panelConfiguration.helpTextKey, this.selectedConfiguration.panelConfiguration.helpTextTitle);
  }

  private mapPanelConfigToSelectableItems(): void {
    if(!this.panelConfig.indexOrdering?.length) return;
    // Define a recursive function to map each IndexOrderingItem to a SelectableItem
    const mapItems = (items: any[]): SelectableItem[] => {
      return items.map(item => {
        const children = item.children ? mapItems(item.children) : [];
        return {
          value: item.value,
          label: item.label ?? item.value,
          children: children?.length > 0 ? children : undefined // Only add children property if it's not empty
        } as SelectableItem;
      });
    };

    // Use the recursive function to transform the entire indexOrdering array
    this.panelAvailableIndexes = mapItems(this.panelConfig.indexOrdering);

    // Define a recursive function to find the first item with no children
    const findFirstItemWithoutChildren = (items: SelectableItem[]): SelectableItem | undefined => {
      for (let item of items) {
        if (!item.children || item.children.length === 0) {
          return item;
        }
        const childItem = findFirstItemWithoutChildren(item.children);
        if (childItem) {
          return childItem;
        }
      }
      return undefined;
    };

    // Find and set the selected item
    this.selectedItem = findFirstItemWithoutChildren(this.panelAvailableIndexes)
      ?? this.panelAvailableIndexes[0];
  }

  onSelectionChange(item: SelectableItem) {
    this.selectedItem = item;
    this.dataTableService.clearData();    
    // find the panel configuration that matches the selected item value
    const panelConfiguration = this.panelConfigs.find(config => config.name === item.value);
    this.selectedConfiguration$.next({ panelConfiguration: panelConfiguration, columnSet: null, options: panelConfiguration.options });
  }

  public get filteredPanelConfigs(): Models.PanelConfiguration[] {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);

    let configs = this.panelConfig.configurations;
    if (panelSelector?.items?.length) {
      configs = panelSelector.items
        .map(item => configs.find(cfg => cfg.name === item))
        .filter(cfg => !!cfg);
    }

    return configs;
  }

  private getInitialData() {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);
    const configs = this.filteredPanelConfigs;
    if (!configs?.length) return null;

    const initialPanelConfig = panelSelector?.defaultItem
      ? (configs.find(cfg => cfg.name === panelSelector.defaultItem) ?? configs[0])
      : configs[0];

    const initialColumnSet = !!initialPanelConfig.elements[0].settings.columnSetNames ? initialPanelConfig.elements[0].settings.columnSetNames[0] : null;

    return { panelConfiguration: initialPanelConfig, columnSet: initialColumnSet, options: initialPanelConfig.options };
  }

  private notifyChildrenAboutUpdatedSettings() {
    this._components.forEach(component => {
      component.options = [...this.settings];  // Make sure a new reference is passed, triggering change detection
    });
  }

  private setsAreEqual(arr1: string[], arr2: string[]): boolean {
    return arr1.length === arr2.length && new Set(arr1).size === new Set(arr2).size &&
           [...new Set(arr1)].every(val => new Set(arr2).has(val));
  }
}
